import { useState } from 'react';
import { Box, Button, Card, Group, Stack, Table, Text } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AnalysisStaff } from '@/api/analyses';
import { CellText } from '@/components/CellText';
import { NAME_MAX_WIDTH, NAME_MIN_WIDTH } from '@/components/tableWidths';
import { StatusBadge } from '@/components/StatusBadge';
import { rowClick, rowStyle } from '@/components/rowClick';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { CommentCount } from '@/features/staff/InternalComments';
import { RunErrorLine } from '@/features/runs/RunErrorLine';
import { InternalStateChip } from '@/features/staff/InternalStateChip';
import { brand } from '@/theme/theme';
import { objectName } from '@/features/objects/objectName';
import { firstLine, runFailure } from '@/features/runs/runError';
import { CancelAnalysisModal } from './CancelAnalysisModal';
import { DeliverModal } from './DeliverModal';

type AnalysisRun = AnalysisStaff['runs'][number];

function latestRun(a: AnalysisStaff): AnalysisRun | undefined {
  return [...a.runs].sort((x, y) => Date.parse(y.created_at) - Date.parse(x.created_at))[0];
}

/** The analysis's newest failed run, whatever state the analysis is in now. */
export function lastFailedRun(a: AnalysisStaff): AnalysisRun | undefined {
  return (a.runs ?? [])
    .filter((r) => r.state === 'failed')
    .reduce<AnalysisRun | undefined>((last, r) => (!last || Date.parse(r.created_at) >= Date.parse(last.created_at) ? r : last), undefined);
}

/** The run a failed analysis failed on: its newest failed run. */
export function failedRun(a: AnalysisStaff): AnalysisRun | undefined {
  return a.state === 'failed' ? lastFailedRun(a) : undefined;
}

function FailedRunLine({ analysis }: { analysis: AnalysisStaff }) {
  const run = failedRun(analysis);
  return run ? <RunErrorLine run={run} /> : null;
}

function boundNames(a: AnalysisStaff): string[] {
  return (a.inputs ?? []).map((i) => (i.data_object ? objectName(i.data_object) : '')).filter(Boolean);
}

/** The bound inputs by name, required slots first as the API orders them. */
export function inputNames(a: AnalysisStaff): string {
  return boundNames(a).join(', ');
}

/** The bound inputs folded for a table cell: the first name and how many more ("Ortofoto +2"). */
export function inputSummary(a: AnalysisStaff): string {
  const [first, ...rest] = boundNames(a);
  if (!first) return '';
  return rest.length ? `${first} +${rest.length}` : first;
}

/** The first bound input, clamped, and a "+N" that never clamps away; every name in the tooltip. */
function InputsCell({ analysis }: { analysis: AnalysisStaff }) {
  const [first, ...rest] = boundNames(analysis);
  if (!first) return <Text fz={13} c={brand.textMuted}>—</Text>;
  return (
    <Group gap={4} wrap="nowrap" align="flex-start" miw={110} maw={220} title={[first, ...rest].join('\n')}>
      <CellText fz={13} c={brand.textMuted} miw={0} title={[first, ...rest].join('\n')} style={{ flex: '0 1 auto' }}>
        {first}
      </CellText>
      {rest.length > 0 && (
        <Text fz={13} fw={600} c={brand.textMuted} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          +{rest.length}
        </Text>
      )}
    </Group>
  );
}

/** The newest failed run's error as one line, for sorting: its code and message's first line. */
export function lastError(a: AnalysisStaff): string | null {
  const run = lastFailedRun(a);
  if (!run) return null;
  const f = runFailure(run.error);
  return [f?.code, firstLine(f?.message)].filter(Boolean).join(': ');
}

/**
 * Staff table over a project's analyses: name, type, both states, created, delivered. With
 * `actions` (the project page), an analysis in review is delivered or marked defective from its
 * row, through the analysis page's own modals; a cancelled one shows its internal reason. With
 * `details`, three more columns: the bound inputs, the current step with its progress, and the
 * last error (UX review §2.3), which then holds the failure line otherwise shown under the state.
 * To keep the table within the page at 1280 px, `details` folds the type under the name and the
 * client badge under the internal state, and every free-text cell clamps (`CellText`).
 */
export function AnalysesTable({
  analyses,
  commentCounts,
  actions = false,
  details = false,
}: {
  analyses: AnalysisStaff[];
  commentCounts?: Record<string, number>;
  actions?: boolean;
  details?: boolean;
}) {
  const { t, i18n } = useTranslation('analyses');
  const lang = i18n.language as 'cs' | 'en';
  const { date, number } = useFormat();
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(analyses, {
    name: (a) => a.name[lang] || a.name.cs,
    type: (a) => a.type_name[lang] || a.type_name.cs,
    state: (a) => a.state,
    status: (a) => t(`projects:status.analysis.${a.client_status}`, { defaultValue: a.client_status }),
    created: (a) => Date.parse(a.created_at),
    delivered: (a) => a.delivered_at ? Date.parse(a.delivered_at) : null,
    inputs: (a) => inputNames(a) || null,
    step: (a) => (a.current_step ?? null),
    error: (a) => lastError(a),
  });
  const stepText = (a: AnalysisStaff) => {
    if (a.state !== 'running' || a.current_step == null) return '—';
    const run = latestRun(a);
    const parts = [t('details.step', { n: a.current_step + 1 })];
    if (run?.current_stage) parts.push(run.current_stage);
    if (typeof run?.progress === 'number') parts.push(`${number(run.progress * 100)} %`);
    return parts.join(' · ');
  };
  const [delivering, setDelivering] = useState<AnalysisStaff | null>(null);
  const [rejecting, setRejecting] = useState<AnalysisStaff | null>(null);
  return (
    <Card p={0} style={{ overflowX: 'auto' }}>
      <Table horizontalSpacing={details ? 'xs' : undefined}>
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('name')}>{t('columns.name')}</SortableTh>
            {!details && <SortableTh {...sortProps('type')}>{t('columns.type')}</SortableTh>}
            <SortableTh {...sortProps('state')}>{t('columns.state')}</SortableTh>
            {!details && <SortableTh {...sortProps('status')}>{t('columns.clientStatus')}</SortableTh>}
            <SortableTh {...sortProps('created')}>{t('columns.created')}</SortableTh>
            <SortableTh {...sortProps('delivered')}>{t('columns.delivered')}</SortableTh>
            {details && <SortableTh {...sortProps('inputs')}>{t('details.inputs')}</SortableTh>}
            {details && <SortableTh {...sortProps('step')}>{t('details.step_column')}</SortableTh>}
            {details && <SortableTh {...sortProps('error')}>{t('details.lastError')}</SortableTh>}
            {actions && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((a) => (
            <Table.Tr key={a.public_id} style={rowStyle} onClick={rowClick(() => void navigate({ to: '/staff/analyses/$analysisId', params: { analysisId: a.public_id } }))}>
              <Table.Td>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <CellText lines={2} fz={14} fw={600} miw={NAME_MIN_WIDTH} maw={NAME_MAX_WIDTH} title={a.name[lang] || a.name.cs}>
                    <Link to="/staff/analyses/$analysisId" params={{ analysisId: a.public_id }} style={{ color: brand.text, textDecoration: 'none' }}>
                      {a.name[lang] || a.name.cs}
                    </Link>
                  </CellText>
                  <CommentCount count={commentCounts?.[a.public_id]} />
                </Group>
                {details && (
                  <Text fz={12} c={brand.textMuted}>
                    {a.type_name[lang] || a.type_name.cs}
                  </Text>
                )}
              </Table.Td>
              {!details && <Table.Td>{a.type_name[lang] || a.type_name.cs}</Table.Td>}
              <Table.Td>
                {details ? (
                  // Both states in one column, the client's badge under the internal chip, so the detail columns fit.
                  <Stack gap={6} align="flex-start">
                    <InternalStateChip state={a.state ?? ''} />
                    <StatusBadge status={a.client_status} scope="analysis" />
                  </Stack>
                ) : (
                  <>
                    <InternalStateChip state={a.state ?? ''} />
                    <FailedRunLine analysis={a} />
                  </>
                )}
              </Table.Td>
              {!details && <Table.Td><StatusBadge status={a.client_status} scope="analysis" /></Table.Td>}
              <Table.Td><Text fz={13} c={brand.textMuted} style={{ whiteSpace: 'nowrap' }}>{date(a.created_at)}</Text></Table.Td>
              <Table.Td><Text fz={13} c={brand.textMuted} style={{ whiteSpace: 'nowrap' }}>{a.delivered_at ? date(a.delivered_at) : '—'}</Text></Table.Td>
              {details && (
                <Table.Td>
                  <InputsCell analysis={a} />
                </Table.Td>
              )}
              {details && (
                <Table.Td>
                  <CellText lines={2} fz={13} c={brand.textMuted} miw={90} maw={200}>
                    {stepText(a)}
                  </CellText>
                </Table.Td>
              )}
              {details && (
                <Table.Td>
                  {(() => {
                    const run = lastFailedRun(a);
                    return run ? (
                      <Box miw={140} maw={280}>
                        <RunErrorLine run={run} lines={2} />
                      </Box>
                    ) : (
                      <Text fz={13} c={brand.textMuted}>—</Text>
                    );
                  })()}
                </Table.Td>
              )}
              {actions && (
                <Table.Td>
                  {a.state === 'review' ? (
                    // Clicks never bubble: the modals are portals, but React events still climb to the row.
                    <Group gap={4} onClick={(e) => e.stopPropagation()}>
                      <Button size="xs" onClick={() => setDelivering(a)}>
                        {t('actions.deliver')}
                      </Button>
                      <Button size="xs" variant="subtle" color="red" onClick={() => setRejecting(a)}>
                        {t('actions.markDefective')}
                      </Button>
                    </Group>
                  ) : a.state === 'cancelled' && a.cancel_reason ? (
                    <CellText lines={2} fz={13} c={brand.textMuted} miw={120} maw={240}>
                      {t('defective.reasonShown', { reason: a.cancel_reason })}
                    </CellText>
                  ) : null}
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {delivering && <DeliverModal analysis={delivering} opened onClose={() => setDelivering(null)} />}
      {rejecting && <CancelAnalysisModal analysis={rejecting} opened defective onClose={() => setRejecting(null)} />}
    </Card>
  );
}
